import {
  ChangeDetectionStrategy, Component, DestroyRef, Inject, OnInit, Optional,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateModule } from '@ngx-translate/core';
import {
  ButtonModule, DIALOG_DATA, DialogRef, DialogService, IconModule, SimpleInputModule,
} from '@hglobal/common';
import { SigninWithCodeData } from '@pt/models';
import { ActionsSubject, Store } from '@ngrx/store';
import { ofType } from '@ngrx/effects';
import { filter, tap } from 'rxjs';
import { SmallLogoComponent } from '@pt/components';
import { environment } from '@pt/environment';

import { AuthActions } from '../../store';
import { TfaResetComponent } from '../tfa-reset/tfa-reset.component';

@Component({
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    IconModule,
    SimpleInputModule,
    SmallLogoComponent,
  ],
  selector: 'pu-tfa',
  templateUrl: './tfa.component.html',
  styleUrls: ['./tfa.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TfaComponent implements OnInit {
  token: string;
  isLoading = false;

  form = this._fb.group({
    code: ['', Validators.required],
  });
  errorDictionary: Record<string, string> = {
    apiValidation: 'errors.invalid_two_factor_code',
  };

  constructor(
    private _fb: FormBuilder,
    private _store: Store,
    private _actionsSubject: ActionsSubject,
    private _dialogRef: DialogRef,
    private _destroyRef: DestroyRef,
    private _dialog: DialogService,
    @Optional() @Inject(DIALOG_DATA) public data: Pick<SigninWithCodeData, 'token'>,
  ) {
    this.token = data.token;
  }

  ngOnInit() {
    this._actionsSubject
      .pipe(
        ofType(AuthActions.verifyCode.succeededAction),
        tap(() => {
          this.isLoading = false;
          this._dialogRef.close();
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();

    this._actionsSubject
      .pipe(
        ofType(AuthActions.verifyCode.failedAction),
        tap(() => (this.isLoading = false)),
        filter(({ error }) => error.code === 'invalid_two_factor_code'),
        tap(() => this.form.controls.code.setErrors({ apiValidation: true })),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  verify(): void {
    this.isLoading = true;
    this._store.dispatch(AuthActions.verifyCode.action({
      payload: {
        ...this.form.value,
        token: this.token,
      },
    }));
  }

  openResetDialog() {
    const panelClass = environment.shortName === 'pt'
      ? 'pu-tfa__hexagonal-background-panel'
      : 'pu-overlay-panel';

    this._dialogRef.close();
    this._dialog.open(TfaResetComponent, { token: this.token }, { panelClass });
  }
}
